import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/api/client";
import type {
  DocumentRequirementMatrix, RoleDocumentRequirement,
  TaskDocumentRequirement,
} from "@/api/types";

const KEY = ["document-requirements"] as const;

export function useDocumentRequirements() {
  return useQuery<DocumentRequirementMatrix>({
    queryKey: KEY,
    queryFn: () => apiRequest("/api/document-requirements"),
  });
}

interface CreateRolePayload {
  role: string;
  document_type: string;
  notes?: string | null;
}

export function useCreateRoleRequirement() {
  const qc = useQueryClient();
  return useMutation<RoleDocumentRequirement, Error, CreateRolePayload>({
    mutationFn: (payload) =>
      apiRequest("/api/document-requirements/by-role", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateRoleRequirement() {
  const qc = useQueryClient();
  return useMutation<
    RoleDocumentRequirement, Error, { id: string; notes: string | null }
  >({
    mutationFn: ({ id, notes }) =>
      apiRequest(`/api/document-requirements/by-role/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ notes }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteRoleRequirement() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      apiRequest(`/api/document-requirements/by-role/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

interface CreateTaskPayload {
  task_code: string;
  task_label: string;
  document_type: string;
  notes?: string | null;
}

export function useCreateTaskRequirement() {
  const qc = useQueryClient();
  return useMutation<TaskDocumentRequirement, Error, CreateTaskPayload>({
    mutationFn: (payload) =>
      apiRequest("/api/document-requirements/by-task", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateTaskRequirement() {
  const qc = useQueryClient();
  return useMutation<
    TaskDocumentRequirement,
    Error,
    { id: string; task_label?: string; notes?: string | null }
  >({
    mutationFn: ({ id, ...patch }) =>
      apiRequest(`/api/document-requirements/by-task/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteTaskRequirement() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      apiRequest(`/api/document-requirements/by-task/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
